import { Info } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  BudgetFormData,
  CreditFormData,
  MAX_BUDGET,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";
import { Textarea } from "@/components/ui/v2/textarea";

type BudgetFormProps = {
  budget?: number;
  error?: string;
  infoMessage?: string;
  isLoading?: boolean;
  label?: string;
  methods: UseFormReturn<BudgetFormData>;
  onSubmit: (data: BudgetFormData) => void;
};

type BudgetFormWithCommentsProps = {
  budget?: number;
  error?: string;
  infoMessage?: string;
  isLoading?: boolean;
  label?: string;
  methods: UseFormReturn<CreditFormData>;
  onSubmit: (data: CreditFormData) => void;
};

const BudgetForm = ({
  budget,
  error,
  infoMessage,
  isLoading,
  label,
  methods,
  onSubmit,
}: BudgetFormProps) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const handleFormSubmit = handleSubmit((data) => {
    if (isSubmitting || isLoading) {
      return;
    }
    onSubmit(data);
  });

  const isDisabled = isLoading || isSubmitting;

  return (
    <Form {...methods}>
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
        <FormField
          control={control}
          name="budget"
          defaultValue={budget}
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>{label || t("common.forecast")}</FormLabel>
              <FormControl>
                <FormattedCurrencyInput
                  autoFocus
                  placeholder="10"
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  maxValue={MAX_BUDGET}
                  initialValue={budget}
                  disabled={isDisabled}
                />
              </FormControl>
              {(errors.budget?.message || error) && (
                <FormMessage>{errors.budget?.message || error}</FormMessage>
              )}
            </FormItem>
          )}
        />
        {infoMessage && (
          <Alert variant="default">
            <Info className="size-4" />
            <AlertDescription>{infoMessage}</AlertDescription>
          </Alert>
        )}
      </form>
    </Form>
  );
};

export const BudgetFormWithComments = ({
  budget,
  error,
  infoMessage,
  isLoading,
  label,
  methods,
  onSubmit,
}: BudgetFormWithCommentsProps) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const handleFormSubmit = handleSubmit((data) => {
    if (isSubmitting || isLoading) {
      return;
    }
    onSubmit(data);
  });

  const isDisabled = isLoading || isSubmitting;

  return (
    <Form {...methods}>
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
        <FormField
          control={control}
          name="budget"
          defaultValue={budget}
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>{label || t("common.forecast")}</FormLabel>
              <FormControl>
                <FormattedCurrencyInput
                  autoFocus
                  placeholder="10"
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  maxValue={MAX_BUDGET}
                  initialValue={budget}
                  disabled={isDisabled}
                />
              </FormControl>
              {(errors.budget?.message || error) && (
                <FormMessage>{errors.budget?.message || error}</FormMessage>
              )}
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="comments"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>{t("common.comments")}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Invoice N.O 789AC for Adzuna in the month september."
                  disabled={isDisabled}
                  count={{
                    current: field.value ? field.value.length : 0,
                    max: 200,
                  }}
                />
              </FormControl>
              {errors.comments?.message && (
                <FormMessage>{errors.comments.message}</FormMessage>
              )}
            </FormItem>
          )}
        />
        {infoMessage && (
          <Alert variant="default">
            <Info className="size-4" />
            <AlertDescription>{infoMessage}</AlertDescription>
          </Alert>
        )}
      </form>
    </Form>
  );
};

export default BudgetForm;
